import { WouldBlock } from "./errno.ts";

/**
 * Shared state and synchronization primitives for multi-process WASIX execution.
 *
 * State shared across worker threads is stored in SharedArrayBuffer instances,
 * including process lifecycle slots, pipe buffers, and file offsets. Process
 * synchronization uses Atomics.wait and Atomics.notify on a global generation counter.
 */

// ---- the runtime state buffer ---------------------------------------------

const GEN = 0; // Generation counter incremented on state changes
const NEXT_PID = 1;
const HEADER_WORDS = 8;

const SLOT_WORDS = 3;
const SLOT_STATE = 0;
const SLOT_EXIT = 1;
const SLOT_HTTP = 2;
const MAX_PIDS = 1 << 16;

/** SLOT_HTTP value while a request is pending. */
const HTTP_PENDING = 0;
/** SLOT_HTTP value when a network request fails. */
export const HTTP_FAILED = -1;

/**
 * A process slot's state. Each change is one compare-and-swap on this word,
 * because a worker can be terminated between any two of its instructions: a
 * process stopped in a wait stays WAITING until the host marks it exited.
 * EXITING is held while the exit code is written.
 */
export const ProcState = { NONE: 0, RUNNING: 1, EXITED: 2, WAITING: 3, EXITING: 4 } as const;

/** Sleep interval in milliseconds between deadlock checks. */
const POLL_MS = 100;

/** Duration in milliseconds before an all-blocked state is treated as deadlock. */
export const STALL_MS = 5000;

let buffer: SharedArrayBuffer | undefined;
let words: Int32Array | undefined;

/** Return or allocate the shared runtime state buffer. */
export function runtimeState(): SharedArrayBuffer {
    if (buffer === undefined) {
        buffer = new SharedArrayBuffer((HEADER_WORDS + MAX_PIDS * SLOT_WORDS) * 4);
        words = new Int32Array(buffer);
    }
    return buffer;
}

/** Join an existing shared runtime state buffer. */
export function attachRuntimeState(sab: SharedArrayBuffer): void {
    buffer = sab;
    words = new Int32Array(sab);
}

function state(): Int32Array {
    if (words === undefined) runtimeState();
    return words!;
}

/** Increment the generation counter and notify waiting threads of a state change. */
export function progressed(): void {
    const w = state();
    Atomics.add(w, GEN, 1);
    Atomics.notify(w, GEN);
}

/** Allocate the next process ID. */
export function nextPid(): number {
    return (Atomics.add(state(), NEXT_PID, 1) % (MAX_PIDS - 1)) + 1;
}

/** Register a process ID as active in the shared state. */
export function processStarting(pid: number): void {
    Atomics.store(state(), HEADER_WORDS + pid * SLOT_WORDS + SLOT_STATE, ProcState.RUNNING);
}

/**
 * Mark a process exited. Both a stopped run and the process itself can report
 * the same exit, and only the first one counts: the result says whether it
 * was this one.
 */
export function processExited(pid: number, exitCode: number): boolean {
    const w = state();
    const slot = HEADER_WORDS + pid * SLOT_WORDS;
    for (;;) {
        const now = Atomics.load(w, slot + SLOT_STATE);
        if (now !== ProcState.RUNNING && now !== ProcState.WAITING) return false;
        if (Atomics.compareExchange(w, slot + SLOT_STATE, now, ProcState.EXITING) === now) break;
    }
    Atomics.store(w, slot + SLOT_EXIT, exitCode);
    Atomics.store(w, slot + SLOT_STATE, ProcState.EXITED);
    progressed();
    return true;
}

/** Whether any process is running rather than waiting in blockUntil. */
function anyoneRunning(w: Int32Array): boolean {
    for (let pid = 1; pid < MAX_PIDS; pid++) {
        if (Atomics.load(w, HEADER_WORDS + pid * SLOT_WORDS + SLOT_STATE) === ProcState.RUNNING) return true;
    }
    return false;
}

/** The exit code of a process that has finished, or undefined if it has not. */
export function exitStatus(pid: number): number | undefined {
    const w = state();
    const slot = HEADER_WORDS + pid * SLOT_WORDS;
    if (Atomics.load(w, slot + SLOT_STATE) !== ProcState.EXITED) return undefined;
    return Atomics.load(w, slot + SLOT_EXIT);
}

/** Mark an HTTP request as pending for the given process ID. */
export function httpRequested(pid: number): void {
    Atomics.store(state(), HEADER_WORDS + pid * SLOT_WORDS + SLOT_HTTP, HTTP_PENDING);
}

/** Answer a process's HTTP request with a status code or HTTP_FAILED. */
export function httpAnswered(pid: number, status: number): void {
    Atomics.store(state(), HEADER_WORDS + pid * SLOT_WORDS + SLOT_HTTP, status);
    progressed();
}

/** The answer to a process's HTTP request, or undefined while outstanding. */
export function httpStatus(pid: number): number | undefined {
    const status = Atomics.load(state(), HEADER_WORDS + pid * SLOT_WORDS + SLOT_HTTP);
    return status === HTTP_PENDING ? undefined : status;
}

export interface Wait {
    /** The process that waits. Required without a deadline. */
    waiter?: number | undefined;
    /** performance.now() time at which the wait gives up. */
    deadline?: number | undefined;
    /** How long every live process may wait before it is a deadlock. */
    stallMs?: number | undefined;
}

/**
 * Wait until `check` returns a value, a deadline expires, or deadlock is detected.
 *
 * Without a deadline, the waiter counts as waiting for deadlock detection. If
 * no live process has been running for `stallMs`, throws WouldBlock.
 */
export function blockUntil<T>(check: () => T | undefined, what: string, wait: Wait = {}): T | undefined {
    const { waiter, deadline, stallMs = STALL_MS } = wait;
    const counted = deadline === undefined;
    if (counted && waiter === undefined) throw new Error(`waiting for ${what} with no deadline and no waiter`);
    const w = state();
    const slot = HEADER_WORDS + (waiter ?? 0) * SLOT_WORDS + SLOT_STATE;
    let stalled = 0;

    for (;;) {
        // Before the check, so that a change made between the check and the
        // wait leaves the generation different and the wait returns at once.
        const seen = Atomics.load(w, GEN);
        const answer = check();
        if (answer !== undefined) return answer;

        let timeout = POLL_MS;
        if (deadline !== undefined) {
            const left = deadline - performance.now();
            if (left <= 0) return undefined;
            timeout = Math.min(timeout, left);
        }

        if (counted) Atomics.compareExchange(w, slot, ProcState.RUNNING, ProcState.WAITING);
        const result = Atomics.wait(w, GEN, seen, timeout);
        // A timeout with the generation unmoved. If every live process is
        // waiting too, nobody was running to move it.
        const idle = result !== "ok" && Atomics.load(w, GEN) === seen;
        const stuck = counted && idle && !anyoneRunning(w);
        if (counted) Atomics.compareExchange(w, slot, ProcState.WAITING, ProcState.RUNNING);

        stalled = stuck ? stalled + timeout : 0;
        if (stalled >= stallMs) throw new WouldBlock(what);
    }
}

// ---- pipes ----------------------------------------------------------------

const PIPE_LOCK = 0;
const PIPE_HEAD = 1;
const PIPE_LENGTH = 2;
const PIPE_READERS = 3;
const PIPE_WRITERS = 4;
const PIPE_HOST_FED = 5;
const PIPE_HEADER_BYTES = 32;

/** Default pipe buffer capacity in bytes. */
export const PIPE_CAPACITY = 64 * 1024;

/** Pipe implementation backed by a ring buffer in SharedArrayBuffer. */
export class Pipe {
    readonly sab: SharedArrayBuffer;
    readonly #words: Int32Array;
    readonly #data: Uint8Array;

    private constructor(sab: SharedArrayBuffer) {
        this.sab = sab;
        this.#words = new Int32Array(sab, 0, PIPE_HEADER_BYTES / 4);
        this.#data = new Uint8Array(sab, PIPE_HEADER_BYTES);
    }

    static create(capacity = PIPE_CAPACITY): Pipe {
        return new Pipe(new SharedArrayBuffer(PIPE_HEADER_BYTES + capacity));
    }

    /** Attach to an existing pipe buffer. */
    static attach(sab: SharedArrayBuffer): Pipe {
        return new Pipe(sab);
    }

    get capacity(): number {
        return this.#data.length;
    }

    get available(): number {
        return Atomics.load(this.#words, PIPE_LENGTH);
    }

    get readers(): number {
        return Atomics.load(this.#words, PIPE_READERS);
    }

    get writers(): number {
        return Atomics.load(this.#words, PIPE_WRITERS);
    }

    /**
     * Whether the host writes this pipe, as a program's input from outside the
     * guest. A reader waiting on it is waiting for a person or another program,
     * which is not a deadlock however long it takes.
     */
    get fedByHost(): boolean {
        return Atomics.load(this.#words, PIPE_HOST_FED) !== 0;
    }

    markFedByHost(): void {
        Atomics.store(this.#words, PIPE_HOST_FED, 1);
    }

    addReader(n: number): void {
        Atomics.add(this.#words, PIPE_READERS, n);
        progressed();
    }

    addWriter(n: number): void {
        Atomics.add(this.#words, PIPE_WRITERS, n);
        progressed();
    }

    /** Read up to `max` bytes from the pipe. */
    read(max: number): Uint8Array {
        let out = new Uint8Array(0);
        this.#lock();
        try {
            const n = Math.min(max, Atomics.load(this.#words, PIPE_LENGTH));
            if (n === 0) return out;
            const head = Atomics.load(this.#words, PIPE_HEAD);
            out = new Uint8Array(n);
            const first = Math.min(n, this.capacity - head);
            out.set(this.#data.subarray(head, head + first));
            if (first < n) out.set(this.#data.subarray(0, n - first), first);
            Atomics.store(this.#words, PIPE_HEAD, (head + n) % this.capacity);
            Atomics.sub(this.#words, PIPE_LENGTH, n);
            return out;
        } finally {
            this.#unlock();
            if (out.length > 0) progressed();
        }
    }

    /** Write bytes into the pipe, returning the number of bytes written. */
    write(bytes: Uint8Array): number {
        if (bytes.length === 0) return 0;
        let n = 0;
        this.#lock();
        try {
            const length = Atomics.load(this.#words, PIPE_LENGTH);
            n = Math.min(bytes.length, this.capacity - length);
            if (n === 0) return 0;
            const tail = (Atomics.load(this.#words, PIPE_HEAD) + length) % this.capacity;
            const first = Math.min(n, this.capacity - tail);
            this.#data.set(bytes.subarray(0, first), tail);
            if (first < n) this.#data.set(bytes.subarray(first, n), 0);
            Atomics.add(this.#words, PIPE_LENGTH, n);
            return n;
        } finally {
            this.#unlock();
            if (n > 0) progressed();
        }
    }

    #lock(): void {
        while (Atomics.compareExchange(this.#words, PIPE_LOCK, 0, 1) !== 0) {
            Atomics.wait(this.#words, PIPE_LOCK, 1, POLL_MS);
        }
    }

    #unlock(): void {
        Atomics.store(this.#words, PIPE_LOCK, 0);
        Atomics.notify(this.#words, PIPE_LOCK, 1);
    }
}

// ---- open files -----------------------------------------------------------

const FILE_HANDLE = 0;
const FILE_APPEND = 1;
const FILE_POSITION_BYTES = 8; // BigInt64, so 8-aligned
const FILE_BYTES = 16;

/** Open file state shared across processes, storing VFS handle and file offset. */
export class OpenFile {
    readonly sab: SharedArrayBuffer;
    readonly #words: Int32Array;
    readonly #position: BigInt64Array;

    private constructor(sab: SharedArrayBuffer) {
        this.sab = sab;
        this.#words = new Int32Array(sab, 0, FILE_POSITION_BYTES / 4);
        this.#position = new BigInt64Array(sab, FILE_POSITION_BYTES, 1);
    }

    static create(handle: number, append: boolean): OpenFile {
        const file = new OpenFile(new SharedArrayBuffer(FILE_BYTES));
        Atomics.store(file.#words, FILE_HANDLE, handle);
        Atomics.store(file.#words, FILE_APPEND, append ? 1 : 0);
        return file;
    }

    static attach(sab: SharedArrayBuffer): OpenFile {
        return new OpenFile(sab);
    }

    get handle(): number {
        return Atomics.load(this.#words, FILE_HANDLE);
    }

    get append(): boolean {
        return Atomics.load(this.#words, FILE_APPEND) !== 0;
    }

    get position(): number {
        return Number(Atomics.load(this.#position, 0));
    }

    set position(value: number) {
        Atomics.store(this.#position, 0, BigInt(Math.trunc(value)));
    }
}
